import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Careers from './pages/Careers';
import Contact from './pages/Contact';
import InsurancePage from './pages/InsurancePage';
import AdminLeads from './pages/AdminLeads';
import Maintenance from './pages/Maintenance';

// Global Maintenance Mode Flag:
// When true, accessing any page hard-redirects the user to /maintenance.
// Toggle to false anytime to immediately restore normal site operation.
export const IS_UNDER_MAINTENANCE = true;

// Hard redirect check on module load if accessing any route other than /maintenance
if (IS_UNDER_MAINTENANCE && typeof window !== 'undefined' && window.location.pathname !== '/maintenance') {
  window.location.replace('/maintenance');
}

// Scroll to top component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Maintenance Guard: performs a hard browser redirect whenever any path other than /maintenance is accessed
function MaintenanceGuard({ children }) {
  const location = useLocation();

  useEffect(() => {
    if (IS_UNDER_MAINTENANCE && location.pathname !== '/maintenance') {
      window.location.replace('/maintenance');
    }
  }, [location.pathname]);

  if (IS_UNDER_MAINTENANCE && location.pathname !== '/maintenance') {
    return null;
  }

  return children;
}

function App() {
  // When maintenance mode is active, hard redirect and show dedicated Under Maintenance experience
  if (IS_UNDER_MAINTENANCE) {
    return (
      <Router>
        <ScrollToTop />
        <MaintenanceGuard>
          <Routes>
            <Route path="/maintenance" element={<Maintenance />} />
            {/* Any accessed path will hard redirect to /maintenance */}
            <Route path="*" element={<Navigate to="/maintenance" replace />} />
          </Routes>
        </MaintenanceGuard>
      </Router>
    );
  }

  // Preserved original site routes (active when IS_UNDER_MAINTENANCE is false)
  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/insurance/:insuranceType" element={<InsurancePage />} />
            <Route path="/admin/leads" element={<AdminLeads />} />
            <Route path="*" element={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                  <p className="text-gray-600 mb-6">Page not found</p>
                  <a href="/" className="text-primary-600 hover:text-primary-700 font-medium">
                    Go back home
                  </a>
                </div>
              </div>
            } />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
